import React from "react";
import { useUserSession } from "../auth/UserSessionContext";
import { userRequest, submitUserCommand } from "../auth/userApi";
import { ORIGIN } from "../utils/configs";
import { FAILURE_STATES, groupNotifications, validNotificationRecord } from "./notificationView";

const NotificationContext = React.createContext(null);
const BASE = "/insights/account/trade-notifications";
const MAX_RECORDS = 2000;
const EMPTY_TOASTS = { items: [], overflow: 0 };
function toastReducer(state, action) {
  if (action.type === "reset") return EMPTY_TOASTS;
  if (action.type === "clear-overflow") return { ...state, overflow: 0 };
  if (action.type === "dismiss") return { ...state, items: state.items.filter(toast => toast.key !== action.key) };
  const merged = new Map(state.items.map(toast => [toast.key, toast]));
  action.groups.forEach(group => {
    if (["ACTION_RESOLVED", "ACTION_CANCELLED", "ACTION_EXPIRED", "PRICE_NOTIFY_CONFIRMED", "PRICE_NOTIFY_CANCELLED"].includes(group.latest.event.state)) merged.delete(group.key);
    else if (merged.has(group.key) || action.type === "add") {
      const previous = merged.get(group.key);
      merged.set(group.key, { key: group.key, cursor: group.cursor, record: group.latest,
        shownAt: previous?.record.cursor === group.latest.cursor ? previous.shownAt : Date.now() });
    }
  });
  const items = [...merged.values()];
  return { items: items.slice(-3), overflow: state.overflow + Math.max(0, items.length - 3) };
}

export function NotificationProvider({ children }) {
  const { session, status: sessionStatus } = useUserSession();
  const sessionKey = session?.sessionId || session?.publicUserId;
  const csrfToken = session?.csrfToken;
  const [records, setRecords] = React.useState([]);
  const [readCursor, setReadCursor] = React.useState(0);
  const [readCursors, setReadCursors] = React.useState(new Set());
  const [connection, setConnection] = React.useState("connecting");
  const [gap, setGap] = React.useState(false);
  const [toastState, dispatchToast] = React.useReducer(toastReducer, EMPTY_TOASTS);
  const { items: toasts, overflow } = toastState;
  const [error, setError] = React.useState("");
  const [historyCursor, setHistoryCursor] = React.useState(null);
  const [historyPage, setHistoryPage] = React.useState(null);
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  const [commandNotice, setCommandNotice] = React.useState(null);
  const [commandHistory, setCommandHistory] = React.useState([]);
  const [priceAlertActions, setPriceAlertActions] = React.useState({});
  const control = React.useRef(null);

  // Popup lifetime is independent of hover, focus and whether the center is open.
  React.useEffect(() => {
    if (!toasts.length) return undefined;
    const timer = setTimeout(() => {
      toasts.filter(toast => toast.shownAt + 5_000 <= Date.now()).forEach(toast => dispatchToast({ type: "dismiss", key: toast.key }));
    }, Math.max(0, Math.min(...toasts.map(toast => toast.shownAt + 5_000)) - Date.now()));
    return () => clearTimeout(timer);
  }, [toasts]);
  React.useEffect(() => {
    if (!commandNotice) return undefined;
    const timer = setTimeout(() => setCommandNotice(current => current === commandNotice ? null : current),
      Math.max(0, commandNotice.observedAt + 5_000 - Date.now()));
    return () => clearTimeout(timer);
  }, [commandNotice]);
  React.useEffect(() => {
    if (!overflow) return undefined;
    const timer = setTimeout(() => dispatchToast({ type: "clear-overflow" }), 5_000);
    return () => clearTimeout(timer);
  }, [overflow]);

  React.useEffect(() => {
    setRecords([]); setReadCursor(0); setGap(false); dispatchToast({ type: "reset" }); setError(""); setHistoryCursor(null); setHistoryPage(null); setCommandNotice(null);
    setReadCursors(new Set()); setCommandHistory([]); setPriceAlertActions({});
    setLoadingHistory(false);
    if (sessionStatus !== "authenticated" || !csrfToken || !sessionKey) { setConnection("signed-out"); return undefined; }
    let active = true;
    let socket; let reconnectTimer; let heartbeat; let opening = false; let subscribed = false;
    let attempts = 0; let cursor = 0; let lastMessage = Date.now();
    const seen = new Set();
    const toastTimers = new Set();
    let retained = new Map();
    const tabId = crypto.randomUUID();
    const peers = new Map();
    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(`cryptember-activity-v1-${sessionKey}`);
    const announce = () => channel?.postMessage({ type: "presence", tabId, visible: document.visibilityState === "visible", at: Date.now() });
    const elected = () => {
      if (document.visibilityState !== "visible") return false;
      // Without cross-tab coordination, keep history but do not duplicate transient notices.
      if (!channel) return false;
      const visible = [...peers].filter(([, peer]) => peer.visible && Date.now() - peer.at < 6_000).map(([id]) => id);
      return [tabId, ...visible].sort()[0] === tabId;
    };
    if (channel) channel.onmessage = event => {
      const value = event.data;
      if (value?.type === "presence" && typeof value.tabId === "string" && value.tabId !== tabId) {
        const isNew = !peers.has(value.tabId); peers.set(value.tabId, { visible: value.visible === true, at: Date.now() }); if (isNew) announce();
        if (!elected()) dispatchToast({ type: "reset" });
      }
      if (value?.type === "leave") peers.delete(value.tabId);
      if (value?.type === "read" && Number.isSafeInteger(value.cursor)) {
        setReadCursor(previous => Math.max(previous, value.cursor));
        if (Array.isArray(value.readCursors)) setReadCursors(previous => new Set([...previous, ...value.readCursors.filter(Number.isSafeInteger)].slice(-MAX_RECORDS)));
      }
    };
    const presence = setInterval(announce, 2_000);
    const onVisibility = () => { announce(); if (!elected()) dispatchToast({ type: "reset" }); if (document.visibilityState === "visible" && !socket && active) connect(); };
    document.addEventListener("visibilitychange", onVisibility); announce();

    const receive = (payload, quiet = true) => {
      if (!active) return;
      const incoming = (Array.isArray(payload.events) ? payload.events : []).filter(validNotificationRecord);
      const fresh = incoming.filter(record => !seen.has(record.event.eventId));
      incoming.forEach(record => seen.add(record.event.eventId));
      // A bounded in-memory dedup window; persistent identity and replay are owned by central.
      if (seen.size > 10_000) { seen.clear(); incoming.forEach(record => seen.add(record.event.eventId)); }
      incoming.forEach(record => retained.set(record.event.eventId, record));
      const nextRecords = [...retained.values()].sort((a, b) => b.cursor - a.cursor).slice(0, MAX_RECORDS);
      retained = new Map(nextRecords.map(record => [record.event.eventId, record])); setRecords(nextRecords);
      const latest = groupNotifications(nextRecords);
      dispatchToast({ type: "update", groups: latest });
      incoming.forEach(record => {
        if (["ACTION_RESOLVED", "ACTION_CANCELLED", "ACTION_EXPIRED"].includes(record.event.state)) {
          setCommandNotice(current => current?.commandId === record.event.commandId ? null : current);
        }
      });
      if (Number.isSafeInteger(payload.readCursor)) setReadCursor(previous => Math.max(previous, payload.readCursor));
      if (Array.isArray(payload.readCursors)) setReadCursors(previous => new Set([...previous, ...payload.readCursors.filter(Number.isSafeInteger)].slice(-MAX_RECORDS)));
      if (payload.gap) setGap(true);
      if (!quiet && elected()) {
        const freshIds = new Set(fresh.map(record => record.event.eventId));
        const meaningful = latest.filter(group => freshIds.has(group.latest.event.eventId)).filter(({ latest: record }) => !record.event.historical
          && (["PROCESS_STARTED", "PRICE_NOTIFY_STARTED", "PRICE_NOTIFY_REACHED", "FILLED", "CANCELLED", "ACTION_REQUIRED"].includes(record.event.state) || FAILURE_STATES.has(record.event.state))
          && !(record.event.expiresAt && record.event.expiresAt <= Date.now()));
        if (meaningful.length) {
          const timer = setTimeout(() => {
            toastTimers.delete(timer);
            if (active && elected()) {
              const wanted = new Set(meaningful.map(group => group.latest.event.eventId));
              dispatchToast({ type: "add", groups: groupNotifications([...retained.values()]).filter(group => wanted.has(group.latest.event.eventId)) });
            }
          }, 350);
          toastTimers.add(timer);
        }
      }
    };
    control.current = { sessionKey, receive, active: () => active, broadcastRead: value => channel?.postMessage({ type: "read", cursor: value.readCursor, readCursors: value.readCursors }) };
    const connect = async () => {
      if (!active || socket || opening) return;
      opening = true; subscribed = false; lastMessage = Date.now();
      clearTimeout(reconnectTimer); setConnection(attempts ? "reconnecting" : "connecting");
      try {
        // This authenticated read also detects disabled rollout before opening a socket.
        const history = await userRequest(`${BASE}?cursor=${cursor}&limit=100`);
        if (!active) return;
        receive(history, true);
        if (Number.isSafeInteger(history.cursor)) cursor = Math.max(cursor, history.cursor);
        const url = new URL(`${ORIGIN}${BASE}/stream`); url.protocol = "wss:";
        socket = new WebSocket(url.href);
        socket.onopen = () => {
          if (!active) { socket.close(); return; }
          lastMessage = Date.now(); socket.send(JSON.stringify({ kind: "subscribe", csrfToken, cursor }));
        };
        socket.onmessage = message => {
          if (!active) return;
          if (typeof message.data !== "string" || message.data.length > 2 * 1024 * 1024) { socket.close(1009); return; }
          let payload; try { payload = JSON.parse(message.data); } catch (_) { socket.close(1002); return; }
          lastMessage = Date.now();
          if (payload.kind === "events") {
            receive(payload, payload.replay !== false);
            if (Number.isSafeInteger(payload.cursor)) cursor = Math.max(cursor, payload.cursor);
          }
          if (payload.kind === "ready") {
            subscribed = true; attempts = 0; setConnection("connected"); setError("");
            if (Number.isSafeInteger(payload.readCursor)) setReadCursor(previous => Math.max(previous, payload.readCursor));
          }
          if (payload.kind === "ping") socket.send(JSON.stringify({ kind: "pong" }));
        };
        socket.onerror = () => {};
        socket.onclose = event => {
          socket = null;
          if (!active) return;
          if ([1008, 4001, 4401, 4403].includes(event.code)) {
            retained.clear(); seen.clear(); setConnection("session-expired"); setRecords([]); setHistoryPage(null); setReadCursor(0); dispatchToast({ type: "reset" }); setCommandNotice(null);
            window.dispatchEvent(new Event("cryptember-auth-expired")); return;
          }
          schedule();
        };
      } catch (cause) {
        if (!active) return;
        if ([401, 403].includes(cause?.status)) { retained.clear(); seen.clear(); setConnection("session-expired"); setRecords([]); setHistoryPage(null); setReadCursor(0); dispatchToast({ type: "reset" }); setCommandNotice(null); return; }
        if (cause?.code === "NOTIFICATIONS_DISABLED") { setConnection("disabled"); return; }
        schedule();
      } finally { opening = false; }
    };
    const schedule = () => {
      if (!active) return;
      setConnection("reconnecting"); attempts += 1;
      reconnectTimer = setTimeout(connect, Math.min(30_000, 1_000 * 2 ** Math.min(attempts, 5)) + Math.random() * 500);
    };
    // Application heartbeats expose liveness to browser JavaScript even if an
    // established TCP connection is silently blackholed by the network.
    heartbeat = setInterval(() => {
      if (socket && Date.now() - lastMessage > (subscribed ? 45_000 : 15_000)) {
        const stale = socket; socket = null;
        stale.onopen = null; stale.onmessage = null; stale.onclose = null; stale.onerror = null;
        stale.close(); schedule();
      }
    }, 5_000);
    const onOnline = () => { if (!socket) connect(); };
    const onCommand = event => {
      if (!active || !event.detail?.message) return;
      // Profile revision conflicts belong in the originating save UI, not global activity notices.
      if (event.detail.code === "SNAPSHOT_CONFLICT") {
        setCommandNotice(current => current?.commandId === event.detail.commandId ? null : current);
        return;
      }
      const notice = { ...event.detail, observedAt: Date.now() };
      const quiet = notice.accepted === true && !notice.recovered;
      setCommandHistory(previous => {
        const existing = previous.find(item => item.commandId === notice.commandId);
        if (quiet && !existing) return previous;
        const unchanged = existing?.state === notice.state && existing?.code === notice.code && existing?.message === notice.message;
        return [{ ...notice, read: unchanged && existing?.read === true }, ...previous.filter(item => item.commandId !== notice.commandId)].slice(0, 100);
      });
      if (quiet) setCommandNotice(current => current?.commandId === notice.commandId ? null : current);
      else setCommandNotice(notice);
    };
    window.addEventListener("online", onOnline); window.addEventListener("cryptember-command-result", onCommand);
    connect();
    return () => {
      active = false; control.current = null; clearTimeout(reconnectTimer); clearInterval(heartbeat); clearInterval(presence);
      toastTimers.forEach(timer => clearTimeout(timer));
      retained.clear(); seen.clear(); peers.clear();
      channel?.postMessage({ type: "leave", tabId }); channel?.close(); socket?.close(1000, "Session changed");
      document.removeEventListener("visibilitychange", onVisibility); window.removeEventListener("online", onOnline);
      window.removeEventListener("cryptember-command-result", onCommand);
    };
  }, [sessionKey, csrfToken, sessionStatus]);

  const markRead = React.useCallback(async cursor => {
    const current = control.current;
    try {
      const result = await userRequest(`${BASE}/read`, { method: "POST", csrfToken, body: { cursor } });
      if (current && current === control.current) {
        const nextReadCursor = result.readCursor ?? cursor;
        setReadCursor(previous => Math.max(previous, nextReadCursor)); current.broadcastRead({ readCursor: nextReadCursor }); setError("");
      }
    } catch (_) { if (current === control.current) setError("Could not mark activity read. Please try again."); }
  }, [csrfToken]);

  const markItemRead = React.useCallback(async cursor => {
    const current = control.current;
    if (!current) return;
    try {
      const result = await userRequest(`${BASE}/read`, { method: "POST", csrfToken, body: { itemCursor: cursor } });
      if (current === control.current) {
        current.receive({ events: [], ...result }); current.broadcastRead(result); setError("");
      }
    } catch (_) { if (current === control.current) setError("Could not mark activity read. Please try again."); }
  }, [csrfToken]);

  const loadHistory = React.useCallback(async () => {
    if (loadingHistory) return;
    const current = control.current; setLoadingHistory(true);
    try {
      const payload = await userRequest(`${BASE}?cursor=${historyCursor || 0}&limit=100`);
      if (current && current === control.current) {
        setHistoryPage((payload.events || []).filter(validNotificationRecord));
        if (Number.isSafeInteger(payload.readCursor)) setReadCursor(previous => Math.max(previous, payload.readCursor));
        setHistoryCursor(payload.cursor < payload.highWater ? payload.cursor : -1); setError("");
      }
    } catch (_) { if (current === control.current) setError("Activity history could not be loaded. Please try again."); }
    finally { if (current === control.current) setLoadingHistory(false); }
  }, [historyCursor, loadingHistory]);

  const dismissToast = React.useCallback((key, cursor) => {
    dispatchToast({ type: "dismiss", key });
    if (Number.isSafeInteger(cursor)) markItemRead(cursor);
  }, [markItemRead]);
  const confirmPriceAlert = async group => {
    const id = group.latest.event.priceAlert?.id;
    const current = control.current;
    if (!id || !current || priceAlertActions[id]?.pending || priceAlertActions[id]?.confirmed) return;
    setPriceAlertActions(previous => ({ ...previous, [id]: { pending: true } }));
    try {
      const result = await submitUserCommand({ type: "TRADE_COMMAND", payload: { command: `confirmPriceNotifyWeb id ${id}` } });
      if (current !== control.current) return;
      if (result?.acknowledgement?.accepted !== true) throw new Error("Confirmation was not completed. Check the connection and try again.");
      setPriceAlertActions(previous => ({ ...previous, [id]: { confirmed: true } }));
      dismissToast(group.key, group.cursor);
    } catch (error) {
      if (current === control.current) setPriceAlertActions(previous => ({ ...previous, [id]: { error: error.message || "Could not confirm. Try again." } }));
    }
  };
  const markCommandRead = commandId => {
    setCommandHistory(previous => previous.map(item => item.commandId === commandId ? { ...item, read: true } : item));
    groupNotifications(records).filter(group => group.records.some(record => record.event.commandId === commandId))
      .forEach(group => markItemRead(group.cursor));
  };
  const dismissCommandNotice = () => {
    if (commandNotice) markCommandRead(commandNotice.commandId);
    setCommandNotice(null);
  };
  const groups = React.useMemo(() => groupNotifications(records), [records]);
  const historyGroups = React.useMemo(() => historyPage ? groupNotifications(historyPage) : null, [historyPage]);
  const isUnread = group => group.cursor > readCursor && !readCursors.has(group.cursor)
    && !group.records.some(record => record.cursor === group.cursor && record.read);
  // Durable trading events take precedence over their transient command feedback.
  const retainedCommands = commandHistory.filter(item => !groups.some(group => group.records.some(record => record.event.commandId === item.commandId)));
  const value = { groups: historyGroups || groups, readCursor, connection, gap, toasts, overflow, error, loadingHistory, historyCursor,
    browsingHistory: historyPage !== null, showRecent: () => { setHistoryPage(null); setHistoryCursor(null); },
    latestCursor: groups[0]?.cursor || 0,
    commandNotice, commandHistory: retainedCommands, dismissCommandNotice, markCommandRead, priceAlertActions, confirmPriceAlert,
    markRead, markItemRead, isUnread, loadHistory, dismissToast, clearOverflow: () => dispatchToast({ type: "clear-overflow" }),
    unread: groups.filter(isUnread).length + retainedCommands.filter(item => !item.read).length };
  const current = control.current?.sessionKey === sessionKey && sessionStatus === "authenticated";
  return <NotificationContext.Provider value={current ? value : { ...value, groups: [], toasts: [], unread: 0, commandNotice: null, commandHistory: [], overflow: 0 }}>{children}</NotificationContext.Provider>;
}

export function useNotifications() { return React.useContext(NotificationContext); }
